import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import { formatDate } from '../../utils/dateFormat';
import BiologicsGrid from '../../components/chemistry/BiologicsGrid';
import PageHeader from '../../components/common/PageHeader';

interface BiologicsFile {
  id: number;
  fileName: string;
  originalFileName?: string;
  createdDate: string;
  biologics_count?: number;
  fileExtension: string;
}

// Helper to get file extension
const getFileExtension = (filename: string): string => {
  const match = filename.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
};

// Check if file is a biologics file by extension
const isBiologicsFile = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  return ['fasta', 'fa', 'faa', 'fna', 'ffn', 'pdb'].includes(ext);
};

const OmicsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileParam = searchParams.get('file');
  const [files, setFiles] = useState<BiologicsFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(
    fileParam ? parseInt(fileParam, 10) : null
  );

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      // Get all files and filter for biologics types by extension
      const response = await chemistryApi.getFiles();
      const biologicsFiles = response.filter(
        (f: any) => isBiologicsFile(f.fileName || f.originalFileName || '')
      );

      // Get biologics count for each file
      const filesWithCounts = await Promise.all(
        biologicsFiles.map(async (file: any) => {
          const fileId = typeof file.id === 'string' ? parseInt(file.id, 10) : file.id;
          const ext = getFileExtension(file.fileName || file.originalFileName || '');
          try {
            const countRes = await chemistryApi.getBiologicCount(fileId);
            return {
              id: fileId,
              fileName: file.originalFileName || file.fileName,
              createdDate: file.createdDate,
              biologics_count: countRes.count,
              fileExtension: ext,
            };
          } catch {
            return {
              id: fileId,
              fileName: file.originalFileName || file.fileName,
              createdDate: file.createdDate,
              biologics_count: 0,
              fileExtension: ext,
            };
          }
        })
      );

      setFiles(filesWithCounts);
      // Auto-select file from URL param, or first file if none specified
      if (filesWithCounts.length > 0 && !selectedFileId) {
        const paramId = fileParam ? parseInt(fileParam, 10) : null;
        const target = paramId && filesWithCounts.find(f => f.id === paramId);
        setSelectedFileId(target ? target.id : filesWithCounts[0].id);
      }
    } catch (error) {
      console.error('Error loading biologics files:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFileId, fileParam]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const getFileTypeLabel = (ext: string): string => {
    if (['fasta', 'fa', 'faa', 'fna', 'ffn'].includes(ext)) return 'FASTA';
    if (ext === 'pdb') return 'PDB';
    return ext.toUpperCase();
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Biologics"
        subtitle="FASTA sequences and PDB structures"
        breadcrumbs={[{ label: 'Biologics' }]}
      />

      <div className="flex flex-1 overflow-hidden">
      {/* Sidebar - File list */}
      <div className="w-64 bg-white border-r flex flex-col">

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-16 h-16 mb-5 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7c0 0 3-3 8-3s8 3 8 3M4 12c0 0 3 3 8 3s8-3 8-3M4 17c0 0 3-3 8-3s8 3 8 3" />
                </svg>
              </div>
              <p className="text-base font-semibold text-gray-700 mb-1">No protein data yet</p>
              <p className="text-sm text-gray-400 text-center mb-5">Upload a FASTA or PDB file to start exploring biologics.</p>
              <button
                onClick={() => navigate('/chemistry/files')}
                className="px-5 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Upload File
              </button>
            </div>
          ) : (
            <div className="p-2">
              {files.map((file) => (
                <button
                  key={file.id}
                  onClick={() => setSelectedFileId(file.id)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                    selectedFileId === file.id
                      ? 'bg-teal-50 border border-teal-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                        file.fileExtension === 'pdb'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {getFileTypeLabel(file.fileExtension)}
                    </span>
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {file.fileName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">
                      {formatDate(file.createdDate)}
                    </span>
                    <span className="text-xs text-gray-600">
                      {file.biologics_count} sequence{file.biologics_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="p-4 border-t bg-gray-50">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <div className="text-lg font-semibold text-gray-900">{files.length}</div>
              <div className="text-xs text-gray-500">Files</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {files.reduce((sum, f) => sum + (f.biologics_count || 0), 0)}
              </div>
              <div className="text-xs text-gray-500">Sequences</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content - Biologics grid */}
      <div className="flex-1 p-4 bg-gray-50 overflow-hidden">
        {selectedFileId ? (
          <BiologicsGrid fileId={selectedFileId} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select a file to view sequences
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default OmicsPage;
